import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PlaneTakeoff, Plus, Check, X } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const LEAVE_TYPES = ["Casual", "Sick", "Earned", "Loss of Pay"];
const S = { Pending: "text-yellow-500 bg-yellow-500/10 border-yellow-500/30", Approved: "text-emerald-500 bg-emerald-500/10 border-emerald-500/30", Rejected: "text-destructive bg-destructive/10 border-destructive/30" };
const Badge = ({ s }) => <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase border ${S[s] || S.Pending}`}>{s}</span>;
const daysBetween = (a, b) => a && b ? Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000) + 1) : 1;

export default function LeaveManagement() {
  const [list, setList] = useState([]);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ employee_id: "", leave_type: "Casual", from_date: "", to_date: "", reason: "", attachment: "" });

  const load = () => api.get("/leaves").then(r => setList(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => { load(); api.get("/employees").then(r => setEmps(r.data)).catch(() => {}); }, []);
  const empName = (id) => emps.find(e => e.id === id)?.name || "-";

  const save = async () => {
    try { await api.post("/leaves", { ...f, days: daysBetween(f.from_date, f.to_date) }); toast.success("Leave applied"); setOpen(false); setF({ employee_id: "", leave_type: "Casual", from_date: "", to_date: "", reason: "", attachment: "" }); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const act = async (id, kind) => {
    try { await api.post(`/leaves/${id}/${kind}`); toast.success(kind === "approve" ? "Approved" : "Rejected"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const pg = usePagination(list);
  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><PlaneTakeoff className="w-3 h-3"/> HRMS</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Leave Management</h1>
        </div>
        <Button data-testid="add-leave-btn" onClick={() => setOpen(true)} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs"><Plus className="w-4 h-4 mr-1"/> New Leave</Button>
      </div>
      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="leaves-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr><th className="text-left p-3">Employee</th><th className="text-left p-3">Leave Type</th><th className="text-left p-3">From</th><th className="text-left p-3">To</th><th className="text-right p-3">Days</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No leave applications yet</td></tr>}
            {pg.pageItems.map(r => (
              <tr key={r.id} data-testid={`leave-row-${r.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3">{empName(r.employee_id)}</td><td className="p-3 text-xs">{r.leave_type}</td>
                <td className="p-3 text-xs font-mono">{r.from_date}</td><td className="p-3 text-xs font-mono">{r.to_date}</td>
                <td className="p-3 text-right font-mono">{r.days}</td><td className="p-3"><Badge s={r.status}/></td>
                <td className="p-3 text-right space-x-1">
                  {r.status === "Pending" && <>
                    <Button size="sm" variant="ghost" data-testid={`leave-approve-${r.id}`} onClick={() => act(r.id, "approve")} className="h-7 w-7 p-0 text-emerald-500"><Check className="w-3 h-3"/></Button>
                    <Button size="sm" variant="ghost" data-testid={`leave-reject-${r.id}`} onClick={() => act(r.id, "reject")} className="h-7 w-7 p-0 text-destructive"><X className="w-3 h-3"/></Button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm bg-card border-border max-w-lg" data-testid="leave-dialog">
          <DialogHeader><DialogTitle className="font-display uppercase">New Leave Application</DialogTitle></DialogHeader>
          <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Employee *</Label>
              <Select value={f.employee_id} onValueChange={(v) => setF(x => ({ ...x, employee_id: v }))}>
                <SelectTrigger data-testid="leave-emp" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent>{emps.filter(e => e.status !== "Inactive").map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Leave Type</Label>
              <Select value={f.leave_type} onValueChange={(v) => setF(x => ({ ...x, leave_type: v }))}>
                <SelectTrigger data-testid="leave-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                <SelectContent>{LEAVE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">From Date *</Label><Input data-testid="leave-from" type="date" value={f.from_date} onChange={(e) => setF(x => ({ ...x, from_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">To Date *</Label><Input data-testid="leave-to" type="date" value={f.to_date} onChange={(e) => setF(x => ({ ...x, to_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            </div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Reason</Label><Input data-testid="leave-reason" value={f.reason} onChange={(e) => setF(x => ({ ...x, reason: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Attachment URL</Label><Input data-testid="leave-attachment" value={f.attachment} onChange={(e) => setF(x => ({ ...x, attachment: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="Optional"/></div>
            <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm text-xs uppercase tracking-wider">Cancel</Button><Button data-testid="save-leave-btn" onClick={save} disabled={!f.employee_id || !f.from_date || !f.to_date} className="rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-wider">Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
